import type { ContractFormData } from "./contractSchema";
import type { PreviewEmail, PreviewNotificada, PreviewResponse } from "../types/preview";
import { formatBRL, formatISODateBR } from "./format";
import { maskCPF, parseBRL } from "./masks";
import { CNPJ_A_CONFIRMAR, entryEmails, notificadaFromEntry } from "./registry";
import type { LocalRegistryEntry } from "./registry";

const DEFAULT_ENDERECAMENTO = "Diretoria de Pós-Venda e Gestão de Contratos";

// Espelho local do mapeamento do servidor — usado apenas no fallback offline. CNPJ null = informado inválido.
export const MULTI_REGISTRY: Record<string, LocalRegistryEntry> = {
  "Gramado Parks": {
    razaoSocial: "Gramado Parks Investimentos e Intermediações S.A",
    cnpj: "00.369.161/0001-57",
    emails: ["relacionamento@gramadoparks.com", "juridico@gramadoparks.com"],
    sede: "Gramado/RS",
  },
  "WAM Comercialização": {
    razaoSocial: "WAM Comercialização S/A",
    cnpj: "17.919.649/0001-03",
    emails: ["posvenda@wambrasil.com", "ouvidoria@wambrasil.com"],
    sede: "Goiânia/GO",
  },
  "GAV Resorts": { razaoSocial: "GAV Resorts Gestão de Negócios e Participação Ltda", cnpj: "40.831.644/0001-46", emails: ["posvenda@gavresorts.com.br", "juridico@gavresorts.com.br"], sede: "Goiânia/GO" },
  Natos: { razaoSocial: "Natos Administradora Ltda", cnpj: "20.685.052/0001-56", emails: ["atendimento@natos.com.br"] },
  "Beach Park": { razaoSocial: "Beach Park Hotéis e Turismo S/A", cnpj: "11.805.397/0001-05", emails: ["posvendas@beachpark.com.br", "ouvidoria@beachpark.com.br"], sede: "Aquiraz/CE" },
};

export function resolveLocalNotificada(data: ContractFormData): PreviewNotificada {
  const entry = MULTI_REGISTRY[data.incorporadora];
  if (entry) return notificadaFromEntry(entry, DEFAULT_ENDERECAMENTO);
  const razaoSocial = data.incorporadora === "Outra" ? data.incorporadoraOutra.trim() : data.incorporadora;
  return { razaoSocial, cnpj: CNPJ_A_CONFIRMAR, verificado: false, enderecamento: DEFAULT_ENDERECAMENTO, sede: null };
}

const localEmails = (data: ContractFormData): PreviewEmail[] => entryEmails(MULTI_REGISTRY[data.incorporadora]);

const LOWER = "abcdefghijklmnopqrstuvwxyz";
const pick = (pool: string): string => pool[Math.floor(Math.random() * pool.length)];

function scrambleChar(ch: string): string {
  if (/[0-9]/.test(ch)) return pick("0123456789");
  const upper = ch !== ch.toLowerCase();
  return upper ? pick(LOWER).toUpperCase() : pick(LOWER);
}

export const obfuscateText = (text: string): string =>
  text.replace(/[A-Za-zÀ-ÖØ-öø-ÿ0-9]+/g, (word) => word.split("").map(scrambleChar).join(""));

export function buildNotificationBody(data: ContractFormData, notificada: string, days: number): string[] {
  const nome = data.nomeCompleto.trim();
  const valor = formatBRL(parseBRL(data.valorEntrada));
  const assinatura = formatISODateBR(data.dataAssinatura);
  const cpf = maskCPF(data.cpf);
  return [
    `${nome}, inscrito(a) no CPF sob o nº ${cpf}, vem, por meio da presente, NOTIFICAR EXTRAJUDICIALMENTE ${notificada} acerca do exercício do DIREITO DE ARREPENDIMENTO previsto no Art. 49 da Lei nº 8.078/1990 (Código de Defesa do Consumidor), relativamente ao instrumento particular de cessão de fração de tempo (multipropriedade) firmado em ${assinatura}, cuja contratação se deu fora do estabelecimento comercial da Notificada, em sala de vendas itinerante. A manifestação ocorre em D+${days}, dentro do prazo decadencial de 7 (sete) dias corridos.`,
    `Nos termos do Art. 67-A, § 10, da Lei nº 4.591/1964, incluído pela Lei nº 13.786/2018 (Lei do Distrato), é facultado ao adquirente, nos contratos firmados em estandes de vendas ou fora da sede do incorporador, o direito de arrependimento no prazo improrrogável de 7 (sete) dias, com a devolução de todos os valores antecipados, inclusive a comissão de corretagem, sendo nula qualquer retenção a título de arras, sinal ou entrada (Art. 51, II e IV, do CDC).`,
    `Requer-se o imediato desfazimento do negócio jurídico e a restituição integral da quantia de ${valor}, paga a título de entrada por meio de ${data.formaPagamento}, com cancelamento das parcelas vincendas e estorno junto à instituição financeira, no prazo de 72 (setenta e duas) horas úteis, constituindo-se a Notificada em mora para todos os fins de direito (Art. 397, parágrafo único, do Código Civil).`,
  ];
}

export function generateProtocol(now: Date = new Date()): string {
  const n = Math.floor(1000 + Math.random() * 9000);
  return `NOT-${now.getFullYear()}-${n}`;
}

export function buildLocalPreview(data: ContractFormData, days: number): PreviewResponse {
  const notificada = resolveLocalNotificada(data);
  return {
    protocolo: generateProtocol(),
    emitidoEm: new Date().toISOString(),
    diasDecorridos: days,
    source: "mock",
    notificada,
    notificante: { nome: data.nomeCompleto.trim(), cpfFormatado: maskCPF(data.cpf) },
    emails: localEmails(data),
    paragrafosOfuscados: buildNotificationBody(data, notificada.razaoSocial, days).map(obfuscateText),
  };
}
